import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { getSessionId, markLeadSubmitted, isLeadSubmitted } from '../lib/chatSession';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const initial = { name: '', email: '', country: '', phone: '', project_details: '', product_interest: '' };

export default function LeadCaptureCard({ dark = true, defaultInterest = '', onDone }) {
  const [form, setForm] = useState({ ...initial, product_interest: defaultInterest });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(() => isLeadSubmitted());
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!form.name || !form.email || !form.country || !form.project_details) {
      toast.error('Please complete the required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/chat/lead`, { ...form, session_id: getSessionId() });
      markLeadSubmitted();
      setDone(true);
      toast.success('Thank you. A MODULAROOM specialist will be in touch.');
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Please review your submission.');
    } finally {
      setSubmitting(false);
    }
  };

  const baseInputClass = dark
    ? 'bg-transparent border-b border-white/15 focus:border-[#C7A36A] text-white placeholder:text-white/35 py-2 px-1 outline-none w-full text-sm'
    : 'bg-transparent border-b border-black/15 focus:border-[#C7A36A] text-[#111] placeholder:text-black/35 py-2 px-1 outline-none w-full text-sm';
  const labelClass = dark ? 'text-[10px] tracking-[0.24em] uppercase text-white/50' : 'text-[10px] tracking-[0.24em] uppercase text-black/45';

  if (done) {
    return (
      <div data-testid="lead-success" className={`mt-4 rounded-xl border ${dark ? 'border-white/10 bg-white/[0.03]' : 'border-black/10 bg-white'} p-4 flex items-center gap-3`}>
        <div className="w-9 h-9 rounded-full bg-[#C7A36A] text-[#111] flex items-center justify-center"><Check size={16}/></div>
        <div className={`${dark ? 'text-white' : 'text-[#111]'} text-sm`}>
          Thank you. Your details are with our team.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className={`mt-4 rounded-xl border ${dark ? 'border-white/10 bg-white/[0.03]' : 'border-black/10 bg-white'} p-4`}>
      <div className={`${dark ? 'text-[#C7A36A]' : 'text-[#C7A36A]'} text-[10px] tracking-[0.28em] uppercase`}>Continue the conversation</div>
      <div className={`${dark ? 'text-white' : 'text-[#111]'} font-display text-lg mt-1 mb-3 tracking-tight`}>Share your details</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <div>
          <label className={labelClass}>Name *</label>
          <input data-testid="lead-name" className={baseInputClass} value={form.name} onChange={upd('name')} placeholder="Your name" />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input data-testid="lead-email" type="email" className={baseInputClass} value={form.email} onChange={upd('email')} placeholder="you@company.com" />
        </div>
        <div>
          <label className={labelClass}>Country *</label>
          <input data-testid="lead-country" className={baseInputClass} value={form.country} onChange={upd('country')} placeholder="India / UAE / …" />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <input data-testid="lead-phone" className={baseInputClass} value={form.phone} onChange={upd('phone')} placeholder="Optional" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Project details *</label>
          <textarea data-testid="lead-details" rows={2} className={baseInputClass} value={form.project_details} onChange={upd('project_details')} placeholder="Timeline, location, use case…" />
        </div>
      </div>
      <button data-testid="lead-submit" disabled={submitting} className="mt-4 rounded-full bg-[#C7A36A] text-[#111] px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-60">
        {submitting ? 'Sending…' : 'Send details'}
      </button>
    </form>
  );
}
